import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VirtualList from 'react-tiny-virtual-list';
import AutoSizer from 'react-virtualized-auto-sizer';

import { TemplatePlayer } from '../../types/team';
import { Paginator } from '../Paginator';

import { ItemAssistant, VariantsAssistance } from './ItemsAssistant';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { SelectValue } from '@/types';
import { JugTarjetometro } from '@/types/fieldAssistance';
import { EMPTY_TEMPLATE_PLAYER } from '@/utils';

interface Props extends VariantsAssistance {
  dataPlayersProps: JugTarjetometro[];
  positionSelected: SelectValue;
}

export const TarjetometroTable = ({
  dataPlayersProps,
  positionSelected,
  variant = 'DEFAULT',
}: Props) => {
  const vlistRef = useRef<VirtualList>(null);
  const playerTemplateMap = usePlayerTemplateMap();
  const { isMobile } = useIsDesktop();
  const [contentCurrentPage, setContentCurrentPage] = useState<
    TemplatePlayer[]
  >([]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [positionSelected.value]);

  const fieldPlayersMap = useMemo(() => {
    const map = new Map<number, JugTarjetometro>();
    dataPlayersProps.forEach((player) => {
      map.set(player.idJT, player);
    });
    return map;
  }, [dataPlayersProps]);

  const playersPositionSelected = useMemo(() => {
    const players: TemplatePlayer[] = dataPlayersProps.map((player) => {
      return playerTemplateMap.get(player.idJT) || EMPTY_TEMPLATE_PLAYER;
    });

    const filteredPlayers = players.filter((player) => {
      if (positionSelected.value === 0) {
        return true;
      }
      return player?.p === positionSelected.value;
    });

    return filteredPlayers.sort((playerA, playerB) => {
      const statsA = fieldPlayersMap.get(playerA.id);
      const statsB = fieldPlayersMap.get(playerB.id);
      const totalB = statsB?.totalParaOrdenacion || 0;
      const totalA = statsA?.totalParaOrdenacion || 0;

      return totalB - totalA;
    });
  }, [
    dataPlayersProps,
    playerTemplateMap,
    positionSelected.value,
    fieldPlayersMap,
  ]);

  const getDataFieldPlayer = useCallback(
    (id: number): JugTarjetometro | undefined => {
      return fieldPlayersMap.get(id);
    },
    [fieldPlayersMap]
  );

  if (isMobile) {
    return (
      playersPositionSelected &&
      playersPositionSelected.length > 0 && (
        <div className="h-screen mt-5 rounded-lg bg-white">
          <AutoSizer className="h-full lg:h-[1002px]">
            {({ height, width }) => {
              return (
                <VirtualList
                  ref={vlistRef}
                  height={height}
                  itemCount={playersPositionSelected.length}
                  itemSize={80}
                  renderItem={({ index, style }) => {
                    const player = playersPositionSelected[index];
                    if (player) {
                      return (
                        <div key={index} style={style}>
                          <ItemAssistant.Tarjetometro
                            dataPlayerFieldAssistance={getDataFieldPlayer(
                              player.id
                            )}
                            templatePlayer={player}
                            variant={variant}
                          />
                        </div>
                      );
                    }
                  }}
                  width={width}
                />
              );
            }}
          </AutoSizer>
        </div>
      )
    );
  }

  const handleContentCurrentPage = (players: TemplatePlayer[]) => {
    players && setContentCurrentPage(players);
  };

  if (
    !Array.isArray(playersPositionSelected) ||
    playersPositionSelected.length === 0
  ) {
    return null;
  }

  const itemsToRender =
    Array.isArray(contentCurrentPage) && contentCurrentPage.length > 0
      ? contentCurrentPage
      : playersPositionSelected;

  return (
    <motion.div key="tarjetometro" animate="show" initial="hide">
      <div className="lg:h-auto mt-6 rounded-lg bg-white">
        {itemsToRender.map((player: TemplatePlayer, index: number) =>
          player ? (
            <ItemAssistant.Tarjetometro
              key={`${player.id ?? 'p'}-${index}`}
              dataPlayerFieldAssistance={getDataFieldPlayer(player.id)}
              templatePlayer={player}
              variant={variant}
            />
          ) : (
            ''
          )
        )}

        {playersPositionSelected.length > 10 && (
          <Paginator
            key={`tarjetometro-${positionSelected?.label ?? 'none'}`}
            currentContentPage={handleContentCurrentPage}
            items={playersPositionSelected}
          />
        )}
      </div>
    </motion.div>
  );
};
